// Shared inline styles for the login UI, keyed off @wiser/ui design tokens (--wui-*) with sensible fallbacks
// so the component still looks reasonable in an app that hasn't loaded @wiser/ui styles. Inline styles (not a
// CSS file) keep the package drop-in — no build/import step for the consumer.
import type { CSSProperties } from 'react';

export const overlay: CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '12vh 16px 16px',
};
export const card: CSSProperties = {
  background: 'var(--wui-surface, #ffffff)', color: 'var(--wui-text, #111827)',
  border: '1px solid var(--wui-border, #e5e7eb)', borderRadius: 'var(--wui-radius, 10px)',
  boxShadow: 'var(--wui-shadow, 0 12px 48px rgba(0,0,0,0.4))', width: 'min(400px, 94vw)', padding: 20,
};
export const panel: CSSProperties = {
  ...card, margin: '10vh auto 0',
};
export const title: CSSProperties = { margin: '0 0 4px', fontSize: 18, fontWeight: 600 };
export const sub: CSSProperties = { margin: '0 0 14px', fontSize: 13, color: 'var(--wui-text-muted, #6b7280)' };
export const lbl: CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--wui-text-muted, #6b7280)',
};
export const input: CSSProperties = {
  padding: '8px 10px', borderRadius: 6, border: '1px solid var(--wui-border, #e5e7eb)',
  background: 'var(--wui-surface, #ffffff)', color: 'var(--wui-text, #111827)', font: 'inherit', fontSize: 14,
};
export const warn: CSSProperties = { margin: '2px 0', fontSize: 12, color: 'var(--wui-text-muted, #6b7280)', lineHeight: 1.4 };
export const err: CSSProperties = { margin: '2px 0', fontSize: 13, color: 'var(--wui-danger, #dc2626)' };
export const primaryBtn: CSSProperties = {
  marginTop: 4, padding: '9px 14px', borderRadius: 6, border: '1px solid var(--wui-accent, #2563eb)',
  background: 'var(--wui-accent, #2563eb)', color: 'var(--wui-on-accent, #ffffff)', font: 'inherit',
  fontWeight: 600, cursor: 'pointer',
};
export const switchBtn: CSSProperties = {
  marginTop: 12, border: 'none', background: 'none', color: 'var(--wui-accent, #2563eb)', font: 'inherit',
  fontSize: 13, cursor: 'pointer', padding: 0,
};
export const linkBtn: CSSProperties = {
  border: 'none', background: 'none', color: 'var(--wui-accent, #2563eb)', font: 'inherit',
  fontSize: 13, cursor: 'pointer', padding: 0, alignSelf: 'flex-start',
};
export const secondaryBtn: CSSProperties = {
  marginTop: 4, padding: '9px 14px', borderRadius: 6, border: '1px solid var(--wui-border, #e5e7eb)',
  background: 'var(--wui-surface, #ffffff)', color: 'var(--wui-text, #111827)', font: 'inherit',
  fontWeight: 600, cursor: 'pointer',
};
export const ok: CSSProperties = { margin: '2px 0', fontSize: 13, color: 'var(--wui-success, #16a34a)' };
export const note: CSSProperties = {
  margin: 0, fontSize: 14, lineHeight: 1.45, color: 'var(--wui-text, #111827)',
};
/** The recovery-code display: large, monospaced, selectable. Uses a translucent neutral fill + inherits the
 *  card's text color, so it contrasts in BOTH light and dark themes (never light-on-white when the host sets
 *  a light --wui-text but leaves --wui-surface-alt unset). */
export const codeBox: CSSProperties = {
  display: 'block', width: '100%', boxSizing: 'border-box', margin: '4px 0',
  padding: '12px 14px', borderRadius: 8, border: '1px solid var(--wui-border, rgba(128,128,128,0.35))',
  background: 'var(--wui-surface-alt, rgba(128,128,128,0.14))', color: 'inherit',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 17,
  fontWeight: 700, letterSpacing: '0.08em', textAlign: 'center', userSelect: 'all', wordBreak: 'break-all',
};
/** A small status pill (recovery active / not set). */
export function chip(active: boolean): CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 10px', borderRadius: 999,
    fontSize: 12, fontWeight: 600,
    color: active ? 'var(--wui-success, #16a34a)' : 'var(--wui-text-muted, #6b7280)',
    background: active ? 'var(--wui-success-soft, rgba(22,163,74,0.12))' : 'var(--wui-surface-alt, #f3f4f6)',
    border: `1px solid ${active ? 'var(--wui-success, #16a34a)' : 'var(--wui-border, #e5e7eb)'}`,
  };
}
export const checkboxLbl: CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, lineHeight: 1.4,
  color: 'var(--wui-text, #111827)', cursor: 'pointer',
};
